/**
 * Defensive formatting helpers — never produce "NaN" or "Infinity" strings.
 * Replace ad-hoc `Intl.NumberFormat(...).format(n)` calls across the app to
 * eliminate the NaN-on-undefined-field class of bug at the rendering layer.
 *
 * Ce module feuille (aucune dependance) heberge les PRIMITIVES de la
 * convention francaise/marocaine -- `NBSP` et `formatFrenchNumber` -- sur
 * lesquelles s'appuie le formateur monetaire central `formattedAmount`
 * (@/utils). Une seule convention dans tout le produit :
 * « 625 171,96 MAD ».
 *
 * Le formatage est fait a la main plutot que par `Intl.NumberFormat('fr-FR')`
 * pour rester deterministe : selon la version d'ICU embarquee (navigateur vs
 * Node/jest), `fr-FR` produit tantot U+00A0 tantot U+202F comme separateur de
 * milliers -- deux ecrans pourtant « corrects » divergeaient au caractere pres.
 */

/**
 * Espace insecable (U+00A0) : separateur de milliers de la convention
 * francaise/marocaine, et separateur montant <-> devise. Insecable pour qu'un
 * montant ne soit jamais coupe en fin de ligne dans un tableau.
 */
export const NBSP = '\u00A0';

/**
 * Primitive de formatage numerique FR/MA : milliers separes par une espace
 * insecable, virgule decimale. Implementation manuelle plutot qu'`Intl` :
 * selon la version d'ICU embarquee (navigateur vs Node/jest), `fr-FR` produit
 * tantot U+00A0 tantot U+202F, ce qui rendait deux ecrans « corrects »
 * visuellement differents. `formattedAmount` (@/utils) s'appuie dessus.
 */
export const formatFrenchNumber = (value: number, precision = 2): string => {
  const safePrecision =
    Number.isFinite(precision) && precision >= 0 ? Math.min(precision, 20) : 0;
  const fixed = Math.abs(Number(value) || 0).toFixed(safePrecision);
  const [integer, decimal] = fixed.split('.');
  const grouped = integer.replace(/\B(?=(\d{3})+(?!\d))/g, NBSP);

  return decimal ? `${grouped},${decimal}` : grouped;
};

const safeNumber = (n: number | null | undefined): number => {
  const v = Number(n ?? 0);
  return Number.isFinite(v) ? v : 0;
};

/** "1 234,56 MAD" — used everywhere we render an amount in the UI. */
export const fmtMad = (n: number | null | undefined): string => {
  const value = safeNumber(n);
  const sign = value < 0 && Number(Math.abs(value).toFixed(2)) !== 0 ? '-' : '';

  return `${sign}${formatFrenchNumber(value, 2)}${NBSP}MAD`;
};

/** "1 234,56" — number without currency suffix. */
export const fmtNumber = (n: number | null | undefined, digits = 2): string => {
  const value = safeNumber(n);
  const rounded = Number(Math.abs(value).toFixed(digits));
  const sign = value < 0 && rounded !== 0 ? '-' : '';

  return `${sign}${formatFrenchNumber(value, digits)}`;
};

/** "12,5 %" — accepts a ratio (0.125 → "12,5 %") or a percent value (12.5 → "12,5 %"). */
export const fmtPct = (
  ratio: number | null | undefined,
  asRatio = true,
  digits = 2,
): string => {
  const v = safeNumber(ratio) * (asRatio ? 100 : 1);
  // Decimales significatives seulement : « 12,5 % » et non « 12,50 % ».
  const trimmed = Number(v.toFixed(digits));
  const decimals = (String(trimmed).split('.')[1] || '').length;
  const sign = trimmed < 0 ? '-' : '';

  return `${sign}${formatFrenchNumber(trimmed, Math.min(decimals, digits))}${NBSP}%`;
};

/**
 * Locale-tolerant number parser. Accepts comma OR dot as decimal separator,
 * strips spaces / NBSP / narrow-NBSP / thousands separators, and rejects junk
 * (returns `null`). Use this anywhere a string may have been typed by a user
 * (FR uses `,`, EN uses `.`, AR may use either) before persisting a number.
 */
export const parseNumber = (
  value: string | number | null | undefined,
): number | null => {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  const stripped = String(value)
    .trim()
    .replace(/[\s   ]/g, '');
  if (!stripped) return null;
  const lastDot = stripped.lastIndexOf('.');
  const lastComma = stripped.lastIndexOf(',');
  let normalized: string;
  if (lastDot === -1 && lastComma === -1) {
    normalized = stripped;
  } else if (lastDot === -1) {
    normalized = stripped.replace(/,/g, '.');
  } else if (lastComma === -1) {
    normalized = stripped;
  } else if (lastDot > lastComma) {
    normalized = stripped.replace(/,/g, '');
  } else {
    normalized = stripped.replace(/\./g, '').replace(',', '.');
  }
  const n = Number(normalized);
  return Number.isFinite(n) ? n : null;
};

/** Same as parseNumber but returns `fallback` instead of null. */
export const parseNumberOr = (
  value: string | number | null | undefined,
  fallback: number,
): number => {
  const n = parseNumber(value);
  return n === null ? fallback : n;
};

/** "31/12/2026" — accepts ISO string or Date. */
export const fmtDateFr = (iso: string | Date | null | undefined): string => {
  if (!iso) return '';
  const d = typeof iso === 'string' ? new Date(iso) : iso;
  if (Number.isNaN(d.getTime())) return '';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
};
